import { useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiClient } from "@/services/api";
import type { CreateVehicleRequest, Vehicle } from "@/types";
import { createInitialFormData } from "@/utils/formHelpers";
import { useFormState } from "@/hooks/useFormState";
import { createCrudHooks } from "@/hooks/crudHooksFactory";
import i18n from "@/i18n/config";

export function useVehicles() {
  return useQuery({
    queryKey: ["vehicles"],
    queryFn: () => apiClient.getVehicles(),
  });
}

export function useMyVehicles(enabled = true) {
  return useQuery({
    queryKey: ["vehicles", "my"],
    queryFn: () => apiClient.getVehicles(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled,
  });
}

export function useVehicle(id: string) {
  return useQuery({
    queryKey: ["vehicles", id],
    queryFn: () => apiClient.getVehicle(id),
    enabled: !!id,
  });
}

const vehicleCrudHooks = createCrudHooks({
  create: {
    mutationFn: (vehicleData: CreateVehicleRequest) =>
      apiClient.createVehicle(vehicleData),
    cacheActions: [
      { type: "invalidate", key: ["vehicles"] },
      { type: "invalidate", key: ["dashboard"] },
    ],
    successMessageKey: "pages:vehicles.validation.createSuccess",
    errorMessageKey: "pages:vehicles.validation.createFailed",
  },
  update: {
    mutationFn: ({
      id,
      ...vehicleData
    }: { id: string } & Partial<CreateVehicleRequest>) =>
      apiClient.updateVehicle(id, vehicleData),
    cacheActions: [
      { type: "invalidate", key: ["vehicles"] },
      {
        type: "invalidate",
        key: (updatedVehicle) => ["vehicles", updatedVehicle.id],
      },
      { type: "invalidate", key: ["dashboard"] },
    ],
    successMessageKey: "pages:vehicles.validation.updateSuccess",
    errorMessageKey: "pages:vehicles.validation.updateFailed",
  },
  delete: {
    mutationFn: (id: string) => apiClient.deleteVehicle(id),
    cacheActions: [
      { type: "invalidate", key: ["vehicles"] },
      { type: "invalidate", key: ["dashboard"] },
    ],
    successMessageKey: "pages:vehicles.validation.deleteSuccess",
    errorMessageKey: "pages:vehicles.validation.deleteFailed",
  },
});

export const useCreateVehicle = vehicleCrudHooks.useCreate;
export const useUpdateVehicle = vehicleCrudHooks.useUpdate;
export const useDeleteVehicle = vehicleCrudHooks.useDelete;

// Validation logic extracted as a pure function for stable reference
function validateVehicleData(
  data: { year: string; maker: string; model: string; name: string; description: string },
): Record<string, string> {
  const errors: Record<string, string> = {};

  const yearNum = parseInt(data.year, 10);
  if (!data.year.trim() || isNaN(yearNum)) {
    errors.year = i18n.t("pages:vehicles.validation.yearRequired");
  } else if (yearNum < 1900 || yearNum > new Date().getFullYear() + 5) {
    errors.year = i18n.t("pages:vehicles.validation.yearRange", {
      max: new Date().getFullYear() + 5,
    });
  }

  if (!data.maker.trim()) {
    errors.maker = i18n.t("pages:vehicles.validation.makerRequired");
  } else if (data.maker.length > 255) {
    errors.maker = i18n.t("pages:vehicles.validation.makerTooLong");
  }

  if (!data.model.trim()) {
    errors.model = i18n.t("pages:vehicles.validation.modelRequired");
  } else if (data.model.length > 255) {
    errors.model = i18n.t("pages:vehicles.validation.modelTooLong");
  }

  if (data.name && data.name.length > 255) {
    errors.name = i18n.t("pages:vehicles.validation.nameTooLong");
  }

  if (data.description && data.description.length > 1000) {
    errors.description = i18n.t("pages:vehicles.validation.descriptionTooLong");
  }

  return errors;
}

// Custom hook for managing vehicle form state
export const useVehicleForm = (initialVehicle?: Vehicle) => {
  const getInitialVehicleData = useCallback(
    () =>
      createInitialFormData(
        initialVehicle
          ? { ...initialVehicle, year: String(initialVehicle.year) }
          : undefined,
        {
          year: "",
          maker: "",
          model: "",
          name: "",
          description: "",
        },
      ),
    [initialVehicle],
  );

  const {
    data: vehicleData,
    errors,
    validate,
    updateField,
    resetForm,
  } = useFormState(getInitialVehicleData, validateVehicleData);

  return {
    vehicleData,
    errors,
    validate,
    updateField,
    resetForm,
  };
};
